'use client'

import '@/components/accordion/accordion.css'
import './header.css'

import type { NavItem } from '@/constants'
import type { CmsFestivesTeaser } from '@/utils/getGlobalChrome'
import type { CSSProperties } from 'react'
import { personalMenu, rizeMenu, tabItems } from '@/constants'

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Accordion, Container, Nav, Navbar, Offcanvas } from 'react-bootstrap'
// oxlint-disable-next-line no-restricted-imports -- hydration-safe: see ARCHITECTURE.md trap #1
import { useMediaQuery } from 'react-responsive'
import { usePathname, useRouter } from 'next/navigation'

import alrajhiLogo from '@/assets/images/alrajhi_logo.svg'
import LoginLink from '@/components/headers/LoginLink'
import Link from '@/components/Link'
import { PillRadio } from '@/components/radio'
import { useHasMounted } from '@/hooks/useHasMounted'
import useStore, { usePersistantStore } from '@/stores/Store'
import { isSamePath } from '@/utils/href'
import { DesktopMegaMenu } from './DesktopMegaMenu'
import { getInitialScrolled, getMenuForPath, getTabForPath } from './mainHeader.helpers'
import { MobileMenu } from './MobileMenu'
import { useFestivesTeaser } from './useFestivesTeaser'

type PromotionNavItem = { title: string; path: string }

function MainHeader({
  promotionNavItems = [],
  festivesTeaser = null
}: {
  promotionNavItems?: PromotionNavItem[]
  /** Seeded at build time by layout.tsx -> AppShell; see getGlobalChrome.ts. */
  festivesTeaser?: CmsFestivesTeaser | null
}) {
  const pathname = usePathname()
  const router = useRouter()

  // Build the personal menu with promotions injected at position 4.
  // promotionNavItems is static build-time data passed from the server layout —
  // it never changes after hydration, so the empty dep array is intentional.
  const personalMenuFull = useMemo(() => {
    if (promotionNavItems.length === 0) return personalMenu
    const result = [...personalMenu]
    result.splice(4, 0, {
      id: 5,
      title: 'Promotions',
      parent: '/personal/promotions',
      children: [{ type: 'PROMOTIONS', children: promotionNavItems }]
    })
    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // promotionNavItems is static build-time data — stable for the page lifetime
  // react-responsive can't read matchMedia on the server, so it returns false
  // during SSR. Gate every media query behind hasMounted so the first client
  // render matches the server output, then resolve real breakpoints post-mount.
  const hasMounted = useHasMounted()
  const isMobile = useMediaQuery({ maxWidth: '1199px' }) && hasMounted
  const isTabletSize = useMediaQuery({ minWidth: 768, maxWidth: 1200 }) && hasMounted
  const isMobileSize = useMediaQuery({ maxWidth: 767 }) && hasMounted

  const [isScrolled, setIsScrolled] = useState(() => getInitialScrolled(pathname))
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [style, setStyle] = useState<CSSProperties>({
    opacity: 0,
    visibility: 'hidden',
    top: '100%',
    transition: '0.3s all ease-in'
  })
  const [tabDesktopContent, setTabDesktopContent] = useState(() =>
    getMenuForPath(pathname, personalMenuFull)
  )
  const { showTeaser, desktopImage, mobileImage } = useFestivesTeaser(festivesTeaser)
  const [activeKey, setActiveKey] = useState<string | null>('100')
  const [activeParent, setActiveParent] = useState<number | null>(null)
  const [activeChild, setActiveChild] = useState<NavItem | null>(null)
  const [activeGrandChild, setActiveGrandChild] = useState(0)

  const setTab = useStore(state => state.setTab)

  // The active pill is a pure function of the current route — derived on every
  // render so the static HTML is already correct (no flicker on reload) and a
  // click can't desync it from where you actually are (no double-click needed).
  const activeTab = useMemo(() => getTabForPath(pathname), [pathname])
  const parentRef = useRef<HTMLUListElement>(null)
  const setCreditCardData = usePersistantStore(state => state.setCreditCardData)

  const showLoginDropdown = useMemo(
    () => pathname.startsWith('/business/') || pathname.includes('digital-banking-overview'),
    [pathname]
  )

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (parentRef.current && !parentRef.current.contains(event.target as Node)) {
      setStyle({
        opacity: 0,
        top: '100%',
        visibility: 'hidden',
        transition: '0.3s all ease-in'
      })
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 600) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    if (isSamePath(pathname, '/') || isSamePath(pathname, '/personal/cards/credit-cards')) {
      window.addEventListener('scroll', handleScroll)
      handleScroll()
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    } else if (isSamePath(pathname, '/MY-alrajhi')) {
      if (isMobileSize) {
        setIsScrolled(true)
      } else {
        window.addEventListener('scroll', handleScroll)
        handleScroll()
        return () => {
          window.removeEventListener('scroll', handleScroll)
        }
      }
    } else {
      setIsScrolled(true)
    }
  }, [pathname])

  const convertToMobileMenu = useMemo(
    () =>
      tabDesktopContent.map(content => {
        let children =
          Array.isArray(content.children) && content.children.flatMap(item => item.children ?? [])

        if (content.title === 'Rize') {
          children = rizeMenu
        }

        return {
          id: content.id,
          title: content.title,
          parent: content.parent,
          path: content.path,
          children
        }
      }),
    [tabDesktopContent]
  )

  const handleNavigateHome = useCallback(() => {
    setTab(0)
    setTabDesktopContent(personalMenuFull)
  }, [setTab, personalMenuFull])

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev)
    setActiveParent(null)
    setActiveKey('100')
  }, [])

  // Keep the store tab and the desktop menu in sync with the route. This depends
  // only on the pathname (not on `tab`), so a pill click's own `setTab` can't
  // re-trigger it against a stale pathname and revert the selection.
  useEffect(() => {
    setTab(getTabForPath(pathname))
    setTabDesktopContent(getMenuForPath(pathname, personalMenuFull))
  }, [pathname, personalMenuFull, setTab])

  useLayoutEffect(() => {
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [pathname])

  // Replace the Personal tab's item with personalMenuFull so PillRadio and the
  // mobile accordion both set the menu that includes Promotions.
  const tabItemsWithPromotions = useMemo(
    () => tabItems.map(entry => (entry.id === 0 ? { ...entry, item: personalMenuFull } : entry)),
    [personalMenuFull]
  )

  const filteredTabItems = useMemo(
    () =>
      tabItemsWithPromotions.map(tab => ({
        ...tab,
        item: (tab.item ?? []).filter(menuItem => !(menuItem.showOn === 'desktop' && isMobile))
      })),
    [isMobile, tabItemsWithPromotions]
  )

  return (
    <header style={{ position: 'sticky', top: 0, zIndex: '999' }}>
      <div className={isScrolled ? 'header-white' : 'header-transparent'}>
        {/* d-none d-xl-block: the static export bakes the desktop branch into the
            HTML (isMobile is false at build time), so CSS must hide this strip on
            phones until hydration removes it. Breakpoint matches isMobile (1199px). */}
        {!isMobile && (
          <div className="container p-0 d-none d-xl-block">
            <div
              className={`w-100 d-inline-flex justify-content-between pb-1 ${
                isSamePath(pathname, '/') ? 'horizontal-line-white' : 'horizontal-line-grey'
              }`}
            >
              <div className="pt-2">
                <PillRadio
                  items={tabItemsWithPromotions}
                  setTab={setTab}
                  setTabDesktopContent={setTabDesktopContent}
                  active={activeTab}
                />
              </div>
              {showTeaser && (
                <img
                  src={desktopImage}
                  alt="Festive promotion"
                  className="branding festive-desktop"
                />
              )}
              <div>
                <Navbar expand="lg" className=" d-none d-lg-block">
                  <Container>
                    <Navbar.Toggle aria-controls="basic-navbar-nav" />
                    <Navbar.Collapse id="basic-navbar-nav">
                      <Nav className="me-auto delete-anchor-style">
                        <Nav.Link as={Link} href="/career" active={isSamePath(pathname, '/career')}>
                          Career
                        </Nav.Link>
                        <Nav.Link as={Link} href="/news" active={isSamePath(pathname, '/news')}>
                          News & Announcements
                        </Nav.Link>
                        <Nav.Link
                          as={Link}
                          href="/contact-us"
                          active={isSamePath(pathname, '/contact-us')}
                        >
                          Contact Us
                        </Nav.Link>
                        <Nav.Link target="blank" href="https://www.alrajhibank.com.sa/en">
                          Global Site
                        </Nav.Link>
                      </Nav>
                    </Navbar.Collapse>
                  </Container>
                </Navbar>
              </div>
            </div>
          </div>
        )}

        {/* div, not <header>: a nested <header> is not scoped by sectioning
            content, so it resolves to a second top-level banner landmark on
            every page (axe landmark-no-duplicate-banner). The outer <header>
            above is the single site banner. id/className preserved for CSS. */}
        <div id="header" className={`header d-flex align-items-center py-3`}>
          {/* desktop navigation */}
          <div className="container container-xl position-relative d-flex align-items-center p-sm-0">
            <Link
              href="/"
              onClick={handleNavigateHome}
              aria-label="al rajhi bank home"
              className="logo d-flex align-items-center"
            >
              <img src={alrajhiLogo.src} alt="al rajhi bank" className="branding cursor-pointer" />
              {showTeaser && isTabletSize && (
                <img src={desktopImage} alt="Festive promotion" className="festive-tablet" />
              )}
              {showTeaser && isMobileSize && (
                <img src={mobileImage} alt="Festive promotion" className="festive-mobile" />
              )}
            </Link>

            <DesktopMegaMenu
              tabDesktopContent={tabDesktopContent}
              activeChild={activeChild}
              setActiveChild={setActiveChild}
              activeGrandChild={activeGrandChild}
              setActiveGrandChild={setActiveGrandChild}
              style={style}
              setStyle={setStyle}
              parentRef={parentRef}
              pathname={pathname}
              router={router}
              setCreditCardData={setCreditCardData}
              isMenuOpen={isMenuOpen}
              toggleMenu={toggleMenu}
            />

            <div
              className="btn-getstarted flex-md-shrink-0 ms-auto p-0"
              style={{ background: 'white' }}
            >
              {!isMobile && <LoginLink showLoginDropdown={showLoginDropdown} />}
            </div>
          </div>
          {/* mobile navigation */}
          <MobileMenu
            isMenuOpen={isMenuOpen}
            toggleMenu={toggleMenu}
            convertToMobileMenu={convertToMobileMenu}
            filteredTabItems={filteredTabItems}
            activeKey={activeKey}
            setActiveKey={setActiveKey}
            activeParent={activeParent}
            setActiveParent={setActiveParent}
            setTab={setTab}
            setTabDesktopContent={setTabDesktopContent}
            showLoginDropdown={showLoginDropdown}
            router={router}
          />
        </div>
      </div>
    </header>
  )
}

export default MainHeader
